import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ProfileComponent as BrandProfileComponent } from './brand/profile/profile.component';
import { RatingQueueComponent as BrandRatingQueueComponent } from './brand/rating-queue/rating-queue.component';
import { ProfileComponent as ProductProfileComponent } from './product/profile/profile.component';
import { ProfileComponent as ManufacturerProfileComponent } from './manufacturer/profile/profile.component';
import { ProfileComponent as ModelProfileComponent } from './car-model/profile/profile.component';
import { JwtGuard } from './jwt.guard';
import { AdminGuard } from './admin.guard';
import { LoginComponent } from './login/login.component';
import { MainComponent } from './main/main.component';
import { ManufacturerListComponent } from './manufacturer/list/list.component';
import { CarModelListComponent } from './car-model/car-model-list/car-model-list.component';
import { CarModelTypeListComponent } from './car-model-type/car-model-type-list/car-model-type-list.component';
import { ProfileInfoComponent } from './car-model-type/profile-info/profile-info.component';
import { CartCComponent } from './cart/cart-c/cart-c.component';
import { ProfileComponent } from './car-model-type/profile/profile.component';
import { ListComponent as NewsListComponent } from './news/list/list.component';
import { CreateComponent as NewsCreateComponent } from './news/create/create.component';
import { ListComponent as CityListComponent } from './city/list/list.component';
import { ListComponent as ProviderListComponent } from './provider/list/list.component';
import { ProfileComponent as ProviderProfileComponent } from './provider/profile/profile.component';
import { WarehouseDeliveryComponent } from './provider/warehouse-delivery/warehouse-delivery.component';
import { SettingComponent } from './user/setting/setting.component';
import { MarkupTableComponent } from './markup-table/markup-table.component';
import { TableComponent } from './provider-price/table/table.component';
import { ListComponent as OrgListComponent } from './org/list/list.component';
import { CreateComponent as OrgCreateComponent } from './org/create/create.component';
import { CellManagerComponent } from './warehouse/cell-manager/cell-manager.component';
import { PrinterManagerComponent } from './warehouse/printer-manager/printer-manager.component';
import { ScannerComponent } from './warehouse/scanner/scanner.component';
import { OrderPickingComponent } from './order-staging/order-picking.component';
import { WarehouseManagerComponent } from './warehouse/warehouse-manager/warehouse-manager.component';
import { WarehouseCardComponent } from './warehouse/warehouse-card/warehouse-card.component';
import { WarehouseCareComponent } from './warehouse/care/warehouse-care.component';
import { LegacyCatalogRedirectComponent } from './legacy-catalog-redirect/legacy-catalog-redirect.component';
import { CallJournalComponent } from './ringostat/call-journal/call-journal.component';
import { B2cCallbackJournalComponent } from './b2c-callback/callback-journal.component';
import { B2cAnalyticsComponent } from './b2c-analytics/b2c-analytics.component';

const routes: Routes = [
  { path: 'login', component: LoginComponent },
  {
    path: '',
    component: MainComponent,
    canActivate: [JwtGuard],
    children: [
      {
        path: 'city',
        component: CityListComponent,
        children: [{ path: 'list', component: CityListComponent }]
      },
      {
        path: 'org',
        children: [
          { path: '', redirectTo: 'list', pathMatch: 'full' },
          { path: 'list', component: OrgListComponent },
          { path: 'create', component: OrgCreateComponent },
          { path: 'edit/:id', component: OrgCreateComponent }
        ]
      },
      {
        path: 'news',
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        children: [
          { path: '', component: NewsListComponent },
          { path: 'create', component: NewsCreateComponent },
          { path: ':id/edit', component: NewsCreateComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' }
        ]
      },
      {
        path: 'settings/supplier_analogue_settings',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/docs/supplier-analogue-settings/supplier-analogue-settings.module').then(
            (m) => m.SupplierAnalogueSettingsModule
          )
      },
      { path: 'settings', component: SettingComponent },
      {
        path: 'cart',
        canActivate: [JwtGuard],
        component: CartCComponent
      },
      {
        path: 'calls',
        canActivate: [JwtGuard],
        component: CallJournalComponent
      },
      {
        path: 'callbacks',
        canActivate: [JwtGuard],
        component: B2cCallbackJournalComponent
      },
      {
        path: 'b2c-analytics',
        canActivate: [JwtGuard],
        component: B2cAnalyticsComponent
      },
      {
        path: 'product/catalogue',
        canActivate: [JwtGuard],
        component: LegacyCatalogRedirectComponent
      },
      {
        path: 'provider/product_table',
        canActivate: [JwtGuard],
        component: TableComponent
      },
      {
        path: 'product/catalogue/:search',
        canActivate: [JwtGuard],
        component: LegacyCatalogRedirectComponent
      },
      {
        path: 'catalogue/products',
        canActivate: [JwtGuard],
        component: LegacyCatalogRedirectComponent
      },
      {
        path: 'product/:id',
        canActivate: [JwtGuard],
        component: ProductProfileComponent
      },
      {
        path: 'brand-rating',
        canActivate: [JwtGuard],
        component: BrandRatingQueueComponent
      },
      {
        path: 'brand/:id',
        canActivate: [JwtGuard],
        component: BrandProfileComponent
      },
      {
        path: 'client',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/client/client.module').then((m) => m.ClientModule)
      },
      {
        path: 'customer-accounts',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/customer-account/customer-account-routing.module').then((m) => m.CustomerAccountRoutingModule)
      },
      {
        path: 'car/:id',
        canActivate: [JwtGuard],
        component: ProfileComponent
      },
      {
        path: 'manufacturer',
        canActivate: [JwtGuard],
        children: [
          { path: 'profile/:id', component: ManufacturerProfileComponent },
          { path: 'list', component: ManufacturerListComponent }
        ]
      },
      {
        path: 'model',
        canActivate: [JwtGuard],
        children: [
          { path: 'profile/:id', component: ModelProfileComponent },
          {
            path: 'list/:id',
            component: CarModelListComponent
          }
        ]
      },
      {
        path: 'modification',
        canActivate: [JwtGuard],
        children: [
          { path: 'profile/:id/info', component: ProfileInfoComponent },
          { path: 'profile/:id/items', component: ProfileComponent },
          { path: 'list/:id', component: CarModelTypeListComponent }
        ]
      },
      {
        path: 'catalogue',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/catalogue/catalogue.module').then((m) => m.CatalogueModule)
      },
      {
        path: 'new-catalog',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/new-catalog/new-catalog.module').then((m) => m.NewCatalogModule)
      },
      {
        path: 'procurement',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/procurement/procurement.module').then((m) => m.ProcurementModule)
      },
      {
        path: 'laximo',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/laximo/laximo.module').then((m) => m.LaximoModule)
      },
      {
        path: 'tecdoc',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/tecdoc-catalog/tecdoc-catalog.module').then((m) => m.TecDocCatalogModule)
      },
      {
        path: 'provider',
        canActivate: [JwtGuard],
        children: [
          { path: 'profile/:id', component: ProviderProfileComponent },
          { path: 'warehouse/:id/delivery', component: WarehouseDeliveryComponent },
          { path: 'list', component: ProviderListComponent },
          { path: '', component: ProviderListComponent }
        ]
      },
      {
        path: 'orders',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/order/order.module').then((m) => m.OrderModule)
      },
      {
        path: 'docs',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/docs/docs.module').then((m) => m.DocsModule)
      },
      {
        path: 'users',
        canActivate: [JwtGuard, AdminGuard],
        loadChildren: () => import('src/app/user/user.module').then((m) => m.UserModule)
      },
      {
        path: 'reports',
        canActivate: [JwtGuard],
        loadChildren: () => import('src/app/reports/reports.module').then((m) => m.ReportsModule)
      },
      {
        path: 'parts-selection',
        canActivate: [JwtGuard],
        loadChildren: () =>
          import('src/app/parts-selection/parts-selection.module').then(
            (m) => m.PartsSelectionModule
          )
      },
      {
        path: 'markup_table',
        canActivate: [JwtGuard],
        component: MarkupTableComponent
      },
      {
        path: 'warehouse/list',
        canActivate: [JwtGuard, AdminGuard],
        component: WarehouseManagerComponent
      },
      {
        path: 'warehouse/create',
        canActivate: [JwtGuard, AdminGuard],
        component: WarehouseCardComponent
      },
      {
        path: 'warehouse/edit/:id',
        canActivate: [JwtGuard, AdminGuard],
        component: WarehouseCardComponent
      },
      {
        path: 'warehouse/care',
        canActivate: [JwtGuard],
        component: WarehouseCareComponent
      },
      {
        path: 'warehouse/cells',
        canActivate: [JwtGuard],
        component: CellManagerComponent
      },
      {
        path: 'warehouse/order-picking',
        component: OrderPickingComponent
      },
      {
        path: 'warehouse/scanner',
        canActivate: [JwtGuard],
        component: ScannerComponent
      },
      {
        path: 'warehouse/printers',
        canActivate: [JwtGuard],
        component: PrinterManagerComponent
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
